import { Lock, Star } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { VISIBLE_FISHES, TOTAL_COLLECTION, type FishDef } from '@/data/fish'

interface CodexDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedId: string
  onSelect: (fish: FishDef) => void
  unlockedFish: Set<string>
}

const RARITY_COLORS: Record<string, string> = {
  common: 'bg-gray-100 text-gray-600',
  rare: 'bg-blue-100 text-blue-600',
  epic: 'bg-purple-100 text-purple-600',
  legendary: 'bg-yellow-100 text-yellow-600',
}

const RARITY_LABELS: Record<string, string> = {
  common: '普通',
  rare: '稀有',
  epic: '史诗',
  legendary: '传说',
}

export function CodexDialog({ open, onOpenChange, selectedId, onSelect, unlockedFish }: CodexDialogProps) {
  const unlockedCount = VISIBLE_FISHES.filter(f => unlockedFish.has(f.id)).length
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white/90 backdrop-blur-xl border-white">
        <DialogHeader>
          <DialogTitle className="text-[#2f5d8a] font-black tracking-[0.2em]">
            观赏鱼图鉴 · COLLECTION {unlockedCount}/{TOTAL_COLLECTION}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {VISIBLE_FISHES.map((f) => {
            const isUnlocked = unlockedFish.has(f.id)
            
            if (!isUnlocked) {
              return (
                <div key={f.id} className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-3">
                  <div className="w-16 h-16 rounded-lg bg-slate-200/60 flex items-center justify-center shrink-0 relative">
                    <Lock className="text-slate-400" size={22} />
                    {f.rarity === 'legendary' && (
                      <Star className="absolute top-1 right-1 text-yellow-500" size={12} fill="currentColor" />
                    )}
                  </div>
                  <div>
                    <div className="font-black text-slate-400 text-sm tracking-wider">???</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {f.unlockCondition?.label || '完成特定条件以解锁'}
                    </div>
                    <div className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${RARITY_COLORS[f.rarity]}`}>
                      {RARITY_LABELS[f.rarity]}
                    </div>
                  </div>
                </div>
              )
            }
            
            return (
              <button
                key={f.id}
                onClick={() => { onSelect(f); onOpenChange(false) }}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-all hover:scale-[1.02] relative ${
                  f.id === selectedId ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white'
                }`}
              >
                {f.rarity === 'legendary' && (
                  <Star className="absolute top-2 right-2 text-yellow-500" size={14} fill="currentColor" />
                )}
                <div className="w-16 h-16 rounded-lg bg-sky-100/70 flex items-center justify-center shrink-0">
                  <img src={f.img} alt={f.name} className="w-14 h-14 object-contain" draggable={false} />
                </div>
                <div className="min-w-0">
                  <div className="font-black text-slate-700 text-sm tracking-wider">{f.code} · {f.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{f.species} / {f.trait} / 难度{f.difficulty}</div>
                  <div className="text-xs text-slate-500 mt-1.5 leading-relaxed line-clamp-2">{f.desc}</div>
                  <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${RARITY_COLORS[f.rarity]}`}>
                    {RARITY_LABELS[f.rarity]}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
